import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;
const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAEMS8sV8oi8I6x_v";

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const ContactForm = ({ theme = "dark", fields, messageLabel, submitLabel, successMessage, errorMessage, brand, testidPrefix }) => {
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const isOcean = theme === "ocean";

  useEffect(() => {
    const action = brand === "scuba" ? "contact-scuba" : "contact-consulting";

    const renderWidget = () => {
      if (!turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current !== null) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action,
        callback: (token) => {
          setTurnstileToken(token);
        },
        "expired-callback": () => {
          setTurnstileToken("");
        },
        "error-callback": () => {
          setTurnstileToken("");
        },
      });
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", renderWidget);
      document.head.appendChild(script);
      return () => {
        script.removeEventListener("load", renderWidget);
      };
    }

    if (window.turnstile) {
      renderWidget();
    } else {
      existingScript.addEventListener("load", renderWidget);
      return () => {
        existingScript.removeEventListener("load", renderWidget);
      };
    }
  }, [brand]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!FORMSPREE_ENDPOINT) {
      toast.error("Contact form is not configured.");
      return;
    }
    if (!TURNSTILE_SITE_KEY) {
      toast.error("Turnstile site key is not configured.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the anti-spam verification.");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        FORMSPREE_ENDPOINT,
        {
          ...values,
          brand,
          "cf-turnstile-response": turnstileToken,
        },
        {
          headers: {
            Accept: "application/json",
          },
        },
      );
      toast.success(successMessage);
      setValues({});
    } catch (err) {
      toast.error(errorMessage);
    } finally {
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken("");
      setSending(false);
    }
  };

  const inputClass = `w-full bg-transparent border-b py-3 font-manrope text-sm text-white outline-none transition-colors duration-300 ${
    isOcean ? "border-white/25 focus:border-cyan-300" : "border-white/15 focus:border-brand-cyan"
  }`;

  return (
    <motion.form
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.1 }}
      onSubmit={handleSubmit}
      className="space-y-7"
      data-testid={`${testidPrefix}-form`}
    >
      <input
        type="text"
        name="website"
        value={values.website || ""}
        onChange={(e) => setValues({ ...values, website: e.target.value })}
        className="absolute left-[-9999px] w-px h-px opacity-0"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        data-testid={`${testidPrefix}-honeypot`}
      />
      {fields.map((field) => (
        <div key={field.name}>
          <label className="block font-mono text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">{field.label}</label>
          <input
            type={field.type}
            required={field.required !== false}
            value={values[field.name] || ""}
            onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            className={inputClass}
            data-testid={`${testidPrefix}-input-${field.name}`}
          />
        </div>
      ))}
      <div>
        <label className="block font-mono text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">{messageLabel}</label>
        <textarea
          required
          rows={4}
          value={values.message || ""}
          onChange={(e) => setValues({ ...values, message: e.target.value })}
          className={inputClass}
          data-testid={`${testidPrefix}-input-message`}
        />
      </div>

      <div className="pt-2" data-testid={`${testidPrefix}-turnstile`}>
        <div ref={turnstileContainerRef} />
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid={`${testidPrefix}-submit-button`}
        className={`group inline-flex items-center gap-2 px-7 py-3.5 font-manrope font-semibold text-sm transition-all duration-300 disabled:opacity-60 ${
          isOcean ? "bg-white text-ocean-deep rounded-full hover:bg-cyan-200" : "bg-brand-cyan text-ink hover:bg-white"
        }`}
      >
        {sending ? "..." : submitLabel}
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        )}
      </button>
    </motion.form>
  );
};
